import { useState, useEffect, useCallback } from 'react';
import { formatDate } from '../lib/dates';
import { useCampus } from '../lib/useCampus';
import { api, ApiError, asList } from '../lib/api';
import { labsApi, type Lab, type LabItem, type Breakage } from '../lib/labsApi';

const rupees = (paise: number) => '₹' + (paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 0 });

export function LabsPage() {
  const [labs, setLabs] = useState<Lab[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [newLab, setNewLab] = useState('');
  const [newInCharge, setNewInCharge] = useState('');
  const [instId, setInstId] = useState<string | null>(null);

  const load = useCallback(() => { labsApi.list().then((r) => setLabs(asList(r))).catch(() => setLabs([])); }, []);
  useEffect(load, [load]);
  // resolve an institution id once (labs need one); reuse the students list for it
  /* ⚠ Was: fetch one student and read their campus — "whichever campus the first student row
     happens to belong to". Arbitrary on a multi-campus trust. See lib/useCampus.ts. */
  const { institutionId: campusId } = useCampus();
  useEffect(() => { setInstId(campusId); }, [campusId]);

  const create = async () => {
    if (!newLab.trim() || !instId) return;
    await labsApi.create({ name: newLab.trim(), institutionId: instId, inCharge: newInCharge.trim() || null });
    setNewLab(''); setNewInCharge(''); load();
  };

  const current = labs.find((l) => l.id === active);

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Facilities</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Laboratories</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>
          Equipment inventory and breakage tracking. Lab periods are set in the timetable; breakage charges go to the student's fees.
        </p>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16, maxWidth: 560 }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Add a lab</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input className="input" placeholder="Lab name (e.g. Chemistry Lab)" value={newLab} onChange={(e) => setNewLab(e.target.value)} style={{ flex: '2 1 200px' }} />
          <input className="input" placeholder="In-charge (optional)" value={newInCharge} onChange={(e) => setNewInCharge(e.target.value)} style={{ flex: '1 1 140px' }} />
          <button className="btn btn-accent" disabled={!instId} onClick={create}>Add</button>
        </div>
        {!instId && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>Add a student first so labs can attach to your institution.</div>}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {labs.map((l) => (
          <button key={l.id} className={active === l.id ? 'btn btn-accent' : 'btn'} onClick={() => setActive(l.id)}>
            {l.name} <span style={{ opacity: 0.7, fontSize: 12 }}>({l.itemCount})</span>
          </button>
        ))}
      </div>

      {current && <LabDetail lab={current} />}
    </div>
  );
}

function LabDetail({ lab }: { lab: Lab }) {
  const [tab, setTab] = useState<'inventory' | 'breakage'>('inventory');
  return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <h2 className="h-display" style={{ fontSize: 20, flex: 1 }}>{lab.name}</h2>
        {lab.inCharge && <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>In-charge: {lab.inCharge}</span>}
      </div>
      <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
        <button className={tab === 'inventory' ? 'btn btn-accent' : 'btn'} onClick={() => setTab('inventory')}>Inventory</button>
        <button className={tab === 'breakage' ? 'btn btn-accent' : 'btn'} onClick={() => setTab('breakage')}>Breakage</button>
      </div>
      {tab === 'inventory' ? <Inventory labId={lab.id} /> : <BreakagePanel labId={lab.id} />}
    </div>
  );
}

function Inventory({ labId }: { labId: string }) {
  const [items, setItems] = useState<LabItem[]>([]);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [qty, setQty] = useState('');
  const [cost, setCost] = useState('');

  const load = useCallback(() => { labsApi.items(labId).then((r) => setItems(asList(r))).catch(() => setItems([])); }, [labId]);
  useEffect(load, [load]);

  const add = async () => {
    if (!name.trim()) return;
    await labsApi.addItem({ labId, name: name.trim(), code: code.trim() || null, quantity: Number(qty) || 0, unitCostPaise: Math.round(Number(cost) * 100) || 0, notes: null });
    setName(''); setCode(''); setQty(''); setCost(''); load();
  };

  const restock = async (id: string) => {
    const by = Number(window.prompt('Add how many to stock?'));
    if (by && by > 0) { await labsApi.restock(id, by); load(); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <input className="input" placeholder="Item (e.g. Beaker 250ml)" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: '2 1 180px' }} />
        <input className="input" placeholder="Code" value={code} onChange={(e) => setCode(e.target.value)} style={{ width: 90 }} />
        <input className="input" type="number" placeholder="Qty" value={qty} onChange={(e) => setQty(e.target.value)} style={{ width: 80 }} />
        <input className="input" type="number" placeholder="Unit ₹" value={cost} onChange={(e) => setCost(e.target.value)} style={{ width: 100 }} />
        <button className="btn btn-accent" onClick={add}>Add item</button>
      </div>

      {items.length === 0
        ? <div style={{ color: 'var(--ink-3)', padding: 16 }}>No items yet.</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {items.map((i) => (
              <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', borderBottom: '1px solid var(--line)' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600 }}>{i.name}</span>
                  {i.code && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}> · {i.code}</span>}
                  {i.unitCostPaise > 0 && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}> · {rupees(i.unitCostPaise)} each</span>}
                </div>
                <span style={{ fontWeight: 700, color: i.quantity === 0 ? 'var(--brand,#c1272d)' : 'inherit' }}>{i.quantity} in stock</span>
                <button className="btn" style={{ padding: '2px 10px' }} onClick={() => restock(i.id)}>+ Restock</button>
                <button className="btn" style={{ padding: '2px 8px' }} onClick={async () => { if (window.confirm('Remove item?')) { await labsApi.deleteItem(i.id); load(); } }}>✕</button>
              </div>
            ))}
          </div>}
    </div>
  );
}

function BreakagePanel({ labId }: { labId: string }) {
  const [items, setItems] = useState<LabItem[]>([]);
  const [logs, setLogs] = useState<Breakage[]>([]);
  const [itemId, setItemId] = useState('');
  const [qty, setQty] = useState('1');
  const [reason, setReason] = useState('');
  const [charge, setCharge] = useState(false);
  const [student, setStudent] = useState<{ id: string; fullName: string } | null>(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Array<{ id: string; fullName: string; admissionNumber: string }>>([]);
  const [chargeAmt, setChargeAmt] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    labsApi.items(labId).then((r) => setItems(asList(r))).catch(() => setItems([]));
    labsApi.breakage(labId).then((r) => setLogs(asList(r))).catch(() => setLogs([]));
  }, [labId]);
  useEffect(load, [load]);

  const doSearch = async () => {
    if (search.trim().length < 2) return;
    try { setResults(asList(await api.get(`/api/students?search=${encodeURIComponent(search.trim())}&take=6`))); } catch { setResults([]); }
  };

  const selectedItem = items.find((i) => i.id === itemId);
  const suggested = selectedItem ? selectedItem.unitCostPaise * (Number(qty) || 0) : 0;

  const log = async () => {
    if (!itemId) { setMsg('Pick an item.'); return; }
    if (charge && !student) { setMsg('Pick a student to charge, or turn off charging.'); return; }
    setBusy(true); setMsg(null);
    try {
      await labsApi.logBreakage({
        labItemId: itemId, quantity: Number(qty) || 1, studentId: charge ? student!.id : (student?.id ?? null),
        reason: reason.trim() || null, charge,
        chargePaise: charge ? (chargeAmt ? Math.round(Number(chargeAmt) * 100) : null) : null,
      });
      setItemId(''); setQty('1'); setReason(''); setCharge(false); setStudent(null); setChargeAmt('');
      load();
      setMsg('Breakage logged.');
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Could not log.'); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 16, background: 'var(--surface-2,#f6f6f4)' }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Log a breakage</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <select className="input" value={itemId} onChange={(e) => setItemId(e.target.value)} style={{ flex: '2 1 180px' }}>
            <option value="">Select item…</option>
            {items.map((i) => <option key={i.id} value={i.id} disabled={i.quantity === 0}>{i.name} ({i.quantity} left)</option>)}
          </select>
          <input className="input" type="number" value={qty} onChange={(e) => setQty(e.target.value)} style={{ width: 70 }} />
          <input className="input" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} style={{ flex: '1 1 160px' }} />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 14 }}>
          <input type="checkbox" checked={charge} onChange={(e) => setCharge(e.target.checked)} />
          Charge a student for this breakage (raises a Lab fee → parent's Payments)
        </label>

        {charge && (
          <div style={{ paddingLeft: 8, borderLeft: '2px solid var(--line)', marginBottom: 10 }}>
            {!student ? (
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input className="input" placeholder="Find student" value={search} onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && doSearch()} />
                <button className="btn" onClick={doSearch}>Search</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <strong>{student.fullName}</strong>
                <button className="btn" style={{ padding: '2px 8px' }} onClick={() => setStudent(null)}>Change</button>
              </div>
            )}
            {!student && results.map((s) => (
              <button key={s.id} className="btn" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 4 }}
                onClick={() => { setStudent({ id: s.id, fullName: s.fullName }); setResults([]); }}>
                {s.fullName} · {s.admissionNumber}
              </button>
            ))}
            <div>
              <label className="label">Charge amount (₹) — {suggested > 0 ? `suggested ${rupees(suggested)}` : 'set unit cost to auto-suggest'}</label>
              <input className="input" type="number" placeholder={suggested > 0 ? (suggested / 100).toString() : ''} value={chargeAmt} onChange={(e) => setChargeAmt(e.target.value)} style={{ width: 140 }} />
            </div>
          </div>
        )}

        <button className="btn btn-accent" disabled={busy} onClick={log}>{busy ? 'Logging…' : 'Log breakage'}</button>
        {msg && <span style={{ marginLeft: 12, fontSize: 13, color: 'var(--ink-2)' }}>{msg}</span>}
      </div>

      {logs.length === 0
        ? <div style={{ color: 'var(--ink-3)', padding: 16 }}>No breakage recorded.</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {logs.map((b) => (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', borderBottom: '1px solid var(--line)' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600 }}>{b.itemName} ×{b.quantity}</span>
                  {b.studentName && <span style={{ fontSize: 13, color: 'var(--ink-2)' }}> · {b.studentName}</span>}
                  {b.reason && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{b.reason}</div>}
                </div>
                {b.charged
                  ? <span className="pill" style={{ fontSize: 11 }}>Charged {rupees(b.chargedPaise)}</span>
                  : <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>No charge</span>}
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{formatDate(b.createdAt)}</span>
              </div>
            ))}
          </div>}
    </div>
  );
}
